// Stock Take Storage Service
// Manages saving and loading draft/in-progress stock takes

export interface SavedStockTake {
  id: string;
  reference: string;
  description: string;
  type: 'Full Stock Take' | 'Targeted Count' | 'Cycle Count' | 'Quick Count';
  status: 'Draft' | 'In Progress' | 'Completed';
  currentStep: 1 | 2 | 3;
  createdAt: Date;
  lastModified: Date;
  createdBy: string;
  
  // Step 1 data
  setupData: {
    countDate: string;
    warehouse: string;
    assignedTo: string;
  };
  
  // Step 2 data (for Quick Count)
  countData?: {
    countItems: Array<{
      code: string;
      description: string;
      category: string;
      systemQty: number;
      countedQty: string;
      variance: number;
      location: string;
      unit: string;
      countedBy: string;
      isCounted: boolean;
    }>;
  };
  
  // Step 3 data (review notes)
  reviewData?: {
    notes: string;
  };
}

const STORAGE_KEY = 'stock-takes-drafts';

// Generate unique ID
export const generateStockTakeId = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `ST-${timestamp}-${random}`;
};

// Generate reference number
export const generateStockTakeReference = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0');
  return `ST-${year}${month}${day}-${time}`;
};

// Get all saved stock takes
export const getAllSavedStockTakes = (): SavedStockTake[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    // Convert date strings back to Date objects
    return parsed.map((st: any) => ({
      ...st,
      createdAt: new Date(st.createdAt),
      lastModified: new Date(st.lastModified),
    }));
  } catch (error) {
    console.error('Error loading saved stock takes:', error);
    return [];
  }
};

// Get a specific saved stock take by ID
export const getSavedStockTake = (id: string): SavedStockTake | null => {
  const allStockTakes = getAllSavedStockTakes();
  return allStockTakes.find(st => st.id === id) || null;
};

// Save a stock take
export const saveStockTake = (stockTake: SavedStockTake): void => {
  try {
    const allStockTakes = getAllSavedStockTakes();
    
    // Check if this stock take already exists
    const existingIndex = allStockTakes.findIndex(st => st.id === stockTake.id);
    
    if (existingIndex >= 0) {
      // Update existing
      allStockTakes[existingIndex] = {
        ...stockTake,
        lastModified: new Date(),
      };
    } else {
      // Add new
      allStockTakes.push({
        ...stockTake,
        lastModified: new Date(),
      });
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allStockTakes));
  } catch (error) {
    console.error('Error saving stock take:', error);
    throw new Error('Failed to save stock take');
  }
};

// Delete a saved stock take
export const deleteSavedStockTake = (id: string): void => {
  try {
    const allStockTakes = getAllSavedStockTakes();
    const filtered = allStockTakes.filter(st => st.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting stock take:', error);
    throw new Error('Failed to delete stock take');
  }
};

// Mark a stock take as completed
export const markStockTakeCompleted = (id: string): void => {
  try {
    const allStockTakes = getAllSavedStockTakes();
    const stockTake = allStockTakes.find(st => st.id === id);
    
    if (stockTake) {
      stockTake.status = 'Completed';
      stockTake.lastModified = new Date();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allStockTakes));
    }
  } catch (error) {
    console.error('Error marking stock take as completed:', error);
    throw new Error('Failed to mark stock take as completed');
  }
};

// Clear all saved stock takes (for testing/admin purposes)
export const clearAllSavedStockTakes = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing saved stock takes:', error);
  }
};

// Get statistics about saved stock takes
export const getSavedStockTakeStats = () => {
  const allStockTakes = getAllSavedStockTakes();
  
  return {
    total: allStockTakes.length,
    draft: allStockTakes.filter(st => st.status === 'Draft').length,
    inProgress: allStockTakes.filter(st => st.status === 'In Progress').length,
    completed: allStockTakes.filter(st => st.status === 'Completed').length,
  };
};
